import React, { useState, useEffect } from "react";
import { withAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import Icon from "@/theme/vectors/bundle";
import { useOrderList } from "@/hooks/order/useOrderList";
import { useTranslation } from "@/hooks/useTranslation";
import CmsLayout from "@/theme/layout-cms";
import OrderTable from "@/components/cms/order/OrderTable";
import OrderDetailPanel from "@/components/cms/order/OrderDetailPanel";
import { ORDER_STATUSES, PAGE_SIZES } from "@/utils/order/constants";
import { SelectField } from "@/theme/inputs/select";
import { SearchField } from "@/theme/inputs/textfields";

const OrderListPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchMode, setIsSearchMode] = useState(false);

    const { 
        orders, 
        loading, 
        loadingMore,
        pagination, 
        setPagination,
        fetchOrders, 
        searchOrders,
        updateOrderStatus 
    } = useOrderList();

    // Xử lý khi thay đổi bộ lọc trạng thái
    const handleStatusFilterChange = (value) => {
        setSelectedStatus(value);
        
        if (isSearchMode && searchQuery) {
            // Nếu đang ở chế độ tìm kiếm, sử dụng API search với trạng thái mới
            handleSearchWithAPI(searchQuery, value);
        } else {
            // Ngược lại, sử dụng API list với bộ lọc
            fetchOrders(0, pagination.limit, false, {
                status: value
            });
        }
    };

    // Xử lý tìm kiếm đơn hàng
    const handleSearch = () => {
        if (!searchQuery.trim()) {
            return;
        }
        
        handleSearchWithAPI(searchQuery, selectedStatus);
        setIsSearchMode(true);
    };

    // Hàm tìm kiếm đơn hàng sử dụng API /search
    const handleSearchWithAPI = (term, status) => {
        // Gọi searchOrders với tham số phù hợp
        searchOrders(term, 0, pagination.limit, false, {
            status: status,
            _clearData: true
        });
    };

    // Xử lý hủy tìm kiếm
    const handleCancelSearch = () => {
        setSearchQuery("");
        setIsSearchMode(false);
        // Quay lại hiển thị danh sách đơn hàng bình thường
        fetchOrders(0, pagination.limit, false, {
            status: selectedStatus,
            _clearData: true
        });
    };

    // Xử lý thay đổi giá trị tìm kiếm
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Xử lý khi nhấp vào đơn hàng để xem chi tiết
    const handleOrderClick = (orderId) => {
        console.log("Mở chi tiết đơn hàng:", orderId);
        setSelectedOrderId(orderId);
        setIsPanelOpen(true);
    };

    // Xử lý thay đổi trạng thái đơn hàng
    const handleStatusChange = async (orderId, newStatus) => {
        await updateOrderStatus(orderId, newStatus);
        if (selectedOrderId === orderId) {
            // Nếu đang xem đơn hàng này, cập nhật lại thông tin chi tiết
            // bằng cách đóng và mở lại panel
            setIsPanelOpen(false);
            setTimeout(() => {
                setIsPanelOpen(true);
            }, 100);
        }
    };

    // Xử lý tải thêm đơn hàng
    const handleLoadMore = () => {
        if (pagination.hasMore) {
            if (isSearchMode) {
                // Nếu đang ở chế độ tìm kiếm, gọi API search với trang tiếp theo
                searchOrders(searchQuery, pagination.offset, pagination.limit, true, {
                    status: selectedStatus
                });
            } else {
                // Ngược lại, gọi API list bình thường
                fetchOrders(pagination.offset, pagination.limit, true, {
                    status: selectedStatus
                });
            }
        }
    };

    // Xử lý thay đổi kích thước trang
    const handlePageSizeChange = (event) => {
        const newPageSize = parseInt(event.target.value);
        
        // Reset pagination với kích thước trang mới
        setPagination({
            offset: 0,
            limit: newPageSize,
            hasMore: true,
            total: pagination.total
        });
        
        // Tải lại dữ liệu với kích thước trang mới
        if (isSearchMode) {
            searchOrders(searchQuery, 0, newPageSize, false, {
                status: selectedStatus
            });
        } else {
            fetchOrders(0, newPageSize, false, {
                status: selectedStatus
            });
        }
    };

    // Xử lý làm mới danh sách đơn hàng
    const handleRefresh = () => {
        setIsRefreshing(true);
        
        if (isSearchMode) {
            // Nếu đang ở chế độ tìm kiếm, làm mới kết quả tìm kiếm
            searchOrders(searchQuery, 0, pagination.limit, false, {
                status: selectedStatus,
                _clearData: true
            }).finally(() => {
                setIsRefreshing(false);
            });
        } else {
            // Ngược lại, làm mới danh sách đơn hàng
            fetchOrders(0, pagination.limit, false, {
                status: selectedStatus,
                _clearData: true
            }).finally(() => {
                setIsRefreshing(false);
            });
        }
    };

    // Tải danh sách đơn hàng khi component được mount
    useEffect(() => {
        // Mặc định khi load trang là danh sách các đơn hàng mới nhất, không áp dụng search
        fetchOrders(0, pagination.limit, false);
    }, [fetchOrders]);

    // Chuyển đổi statuses từ constants sang options cho Select component
    const statusOptions = [
        { value: "", label: t("order.filter.allStatuses") || "Tất cả trạng thái" },
        ...Object.keys(ORDER_STATUSES).map((key) => ({
            value: key,
            label: t(`order.statusOptions.${key.toLowerCase()}`) || key,
        })),
    ];

    return (
        <CmsLayout title={t("order.title")}>
            {/* Phần lọc đơn giản */}
            <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleRefresh}
                        disabled={isRefreshing || loading}
                        startIcon={<Icon name="refresh2" spin={isRefreshing} />}
                        sx={{ fontWeight: 'medium', minWidth: '120px' }}
                    >
                        {t("common.refresh")}
                    </Button>
                    
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <SearchField
                            placeholder={t("order.search.placeholder") || "Tìm kiếm theo tên, SĐT..."}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onSearch={handleSearch}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            size="small"
                            sx={{ width: 250 }}
                        />
                        
                        {isSearchMode && (
                            <Tooltip title={t("common.cancelSearch") || "Hủy tìm kiếm"}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    onClick={handleCancelSearch}
                                    sx={{ minWidth: 'auto', p: '5px' }}
                                >
                                    <Icon name="remove" size={16} />
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                
                <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="page-size-label">{t('order.pageSize', 'Số dòng')}</InputLabel>
                        <Select
                            labelId="page-size-label"
                            value={pagination.limit}
                            onChange={handlePageSizeChange}
                            label={t('order.pageSize', 'Số dòng')}
                        >
                            {PAGE_SIZES.map(size => (
                                <MenuItem key={size} value={size}>{size}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <SelectField
                        options={statusOptions}
                        value={selectedStatus}
                        onChange={handleStatusFilterChange}
                        label={t("order.status") || "Trạng thái"}
                        size="small"
                        sx={{ minWidth: 200 }}
                    />
                </Box>
            </Box>

            {/* Bảng danh sách đơn hàng */}
            <OrderTable
                orders={orders}
                loading={loading || isRefreshing}
                onOrderClick={handleOrderClick}
                onStatusChange={handleStatusChange}
                translate={t}
            />

            {/* Nút tải thêm */}
            {pagination.hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                    <Button 
                        variant="outlined" 
                        onClick={handleLoadMore} 
                        disabled={loadingMore}
                        startIcon={loadingMore && <CircularProgress size={20} />}
                    >
                        {loadingMore ? 
                            t('common.loading', 'Đang tải...') : 
                            t('common.loadMore', 'Tải thêm')}
                    </Button>
                </Box>
            )}

            {/* Panel chi tiết đơn hàng */}
            <OrderDetailPanel
                open={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                orderId={selectedOrderId}
                onStatusChange={handleStatusChange}
            />
        </CmsLayout>
    );
};

export default withAuth(OrderListPage);
